import { PARTIAL, READY } from 'components/users/flexlist/FlexList';
import { uniq } from 'lodash-es';
import { DateTime } from 'luxon';
import type {
  DashboardMode,
  Filter,
  State as UsersDashboardState,
  WeeksByUser,
} from 'redux/modules/users-page/users-dashboard/usersDashboard.types';
import {
  closeUserWeekDetailsModal,
  fetchedGraphTotals,
  fetchedUserSummaries,
  resetGraphTotals,
  resetState,
  setFilter,
  setUserSummariesLoadingStatus,
  showUserWeekDetailsModalForUserId,
} from 'redux/modules/users-page/users-dashboard/usersDashboard.types';
import { createXhrStatusModule, xhrStatusFactory } from 'redux/reusable/xhrStatus';
import type { DeepPartial } from 'types/utility';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { keyBy } from 'utils/typesafe';

const { withXhrStatusReducer } = createXhrStatusModule('usersDashboard');

const thresholdBurningDefault = 100;
const thresholdSleepingDefault = 25;

function getDefaultThresholdForMode(mode: DashboardMode | undefined) {
  if (mode === 'burning') {
    return thresholdBurningDefault;
  } else if (mode === 'sleeping') {
    return thresholdSleepingDefault;
  } else {
    return 10;
  }
}

const initialFilterState: Filter = {
  mode: undefined,
  period: 6,
  percent: 10,
  userIds: undefined,
  teamIds: undefined,
  limit: 50,
  page: 1,
  from: DateTime.now().minus({ weeks: 6 }).toISODate(),
  to: DateTime.now().toISODate(),
  timeframe_filter_method: 'any_week',
};

const initialUsersDashboardState: UsersDashboardState = {
  filter: initialFilterState,
  xhrStatus: xhrStatusFactory(),
  // visible users (ids)
  users: [],
  weeksByUser: {},
  totalsByUser: {},
  hasLoadedUsersForDropdown: false,
};

const usersDashboardReducer = reducerWithInitialState(initialUsersDashboardState)
  .caseWithAction(setFilter, (state, action): UsersDashboardState => {
    const { mode, shouldReset, timeframe_filter_method } = action.payload;

    // Only check for timeframe change if the value is actually provided and different
    const timeframeMethodChanged =
      timeframe_filter_method !== undefined &&
      timeframe_filter_method !== state.filter.timeframe_filter_method;

    // Detect any mode change (including to/from undefined)
    const modeChanged = 'mode' in action.payload && mode !== state.filter.mode;

    // Reset list when mode changes OR explicit reset is requested
    const shouldResetList = shouldReset || modeChanged || timeframeMethodChanged;

    // Only calculate threshold override if mode actually changed
    const overrideThreshold: DeepPartial<Filter> = modeChanged
      ? { percent: getDefaultThresholdForMode(action.payload.mode!) }
      : {};

    const newFilter = {
      ...state.filter,
      ...overrideThreshold,
      ...action.payload,
    };

    // Optimize: only create new objects if we're actually resetting
    if (shouldResetList) {
      return {
        ...state,
        filter: newFilter,
        users: [],
        totalsByUser: {},
        weeksByUser: {},
        graphTotals: undefined,
        hasMore: false,
        hasLoadedUsersForDropdown: true,
      };
    }

    // If not resetting, only update the filter (keep hasMore for pagination)
    return {
      ...state,
      filter: newFilter,
      hasLoadedUsersForDropdown: true,
    };
  })
  .caseWithAction(resetState, (state): UsersDashboardState => {
    return {
      ...state,
      filter: initialFilterState,
      users: [],
      totalsByUser: {},
      weeksByUser: {},
      graphTotals: undefined,
      hasMore: false,
      hasLoadedUsersForDropdown: false,
    };
  })
  .caseWithAction(showUserWeekDetailsModalForUserId, (state, action): UsersDashboardState => {
    return {
      ...state,
      userWeekDetailsModal: action.payload,
    };
  })
  .case(closeUserWeekDetailsModal, (state): UsersDashboardState => {
    return {
      ...state,
      userWeekDetailsModal: undefined,
    };
  })
  .case(resetGraphTotals, state => {
    return {
      ...state,
      graphTotals: initialUsersDashboardState.graphTotals,
    };
  })
  .caseWithAction(fetchedGraphTotals, (state, action): UsersDashboardState => {
    return {
      ...state,
      graphTotals: {
        ...state.graphTotals,
        usersCount: action.payload.users,
        capacity: action.payload.capacity,
        weeks: keyBy(action.payload.grouped || [], obj => `${obj.since}-${obj.until}`),
      },
    };
  })
  .caseWithAction(fetchedUserSummaries, (state, action): UsersDashboardState => {
    const freshWeeksByUser = action.payload.result.users.reduce<UsersDashboardState['weeksByUser']>(
      (map, obj) => {
        const userId = obj.id;
        let weeks: WeeksByUser = {};

        if (obj.grouped) {
          weeks = obj.grouped.reduce<WeeksByUser>((result, w) => {
            const weekKey = `${w.since}-${w.until}`;

            result[weekKey] = {
              capacity: w.capacity,
              billable_duration: w.billable_duration,
              non_billable_duration: w.non_billable_duration,
              planned_duration: w.planned_duration,
              tasked_duration: w.tasked_duration,
              duration: w.duration,
              total_duration: w.total_duration,
              week: weekKey,
            };
            return result;
          }, {});
        }
        weeks['lastFetch'] = Date.now();
        if (action.payload.params.hasAccessToTasks) {
          if (action.payload.scope === 'forecasts') {
            weeks['status'] = READY;
          } else {
            weeks['status'] = PARTIAL;
          }
        } else {
          weeks['status'] = READY;
        }
        map[userId] = weeks;

        return map;
      },
      {}
    );

    const freshTotalsByUser = action.payload.result.users.reduce<
      UsersDashboardState['totalsByUser']
    >((map, obj) => {
      const userId = obj.id;
      map[userId] = obj;
      return map;
    }, {});

    // For virtualized fetches, always merge. Otherwise, check if it's page 1 to decide.
    const isFirstPage = !action.payload.isVirtualizedFetch && action.payload.params.page === 1;
    const newUserIds = action.payload.result.users.map(u => u.id);

    return {
      ...state,
      users: isFirstPage ? newUserIds : uniq([...state.users, ...newUserIds]),
      weeksByUser: isFirstPage
        ? freshWeeksByUser
        : {
            ...state.weeksByUser,
            ...freshWeeksByUser,
          },
      totalsByUser: isFirstPage
        ? freshTotalsByUser
        : {
            ...state.totalsByUser,
            ...freshTotalsByUser,
          },
      filter: {
        ...state.filter,
        page: action.payload.maybeHasMore ? state.filter.page + 1 : state.filter.page,
      },
      hasMore: action.payload.maybeHasMore,
    };
  })
  .caseWithAction(setUserSummariesLoadingStatus, (state, action): UsersDashboardState => {
    const weeksByUser = Object.entries(state.weeksByUser).reduce(
      (map, [userId, obj]) => {
        if (action.payload[userId]) {
          map[userId] = { ...obj };
          map[userId].status = action.payload[userId];
        }
        return map;
      },
      {} as UsersDashboardState['weeksByUser']
    );

    return {
      ...state,
      weeksByUser: {
        ...state.weeksByUser,
        ...weeksByUser,
      },
    };
  });

export default withXhrStatusReducer(usersDashboardReducer);
